import { getSupabaseClient } from '@/config/supabase';
import type { Device, TrackingAccuracy, TrackingActivityType } from '@/types';

export async function fetchDevices(organizationIds: string[]): Promise<Device[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc('get_devices_by_organizations', {
    p_organization_ids: organizationIds,
  });
  if (error) throw error;
  // The RPC may not return all new columns — apply safe defaults
  return (data ?? []).map((d: Record<string, unknown>) => ({
    ...d,
    permission_status: d.permission_status ?? 'unknown',
    is_precise_location: d.is_precise_location ?? false,
    permission_updated_at: d.permission_updated_at ?? null,
    tracking_accuracy: d.tracking_accuracy ?? null,
    tracking_activity_type: d.tracking_activity_type ?? null,
    tracking_distance_interval_m: d.tracking_distance_interval_m ?? null,
    os_version: d.os_version ?? null,
    archived_at: d.archived_at ?? null,
    phone_number: d.phone_number ?? null,
    serial_number: d.serial_number ?? null,
  })) as Device[];
}

export async function fetchDeviceById(deviceId: string): Promise<Device | null> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('devices')
    .select('id, device_id, device_name, organization_id, first_name, last_name, device_type, last_checkin_at, last_latitude, last_longitude, battery_level, app_version, os_version, is_active, tracking_accuracy, tracking_activity_type, tracking_distance_interval_m, permission_status, is_precise_location, permission_updated_at, archived_at, phone_number, serial_number, organizations(name)')
    .eq('id', deviceId)
    .single();
  if (error) throw error;
  if (!data) return null;
  const organizations = data.organizations as unknown as { name: string } | null;
  return {
    ...data,
    organization_name: organizations?.name ?? '',
  } as Device;
}

export async function updateTrackingSettings(
  deviceId: string,
  settings: {
    tracking_accuracy: TrackingAccuracy | null;
    tracking_activity_type: TrackingActivityType | null;
    tracking_distance_interval_m: number | null;
  }
): Promise<void> {
  const supabase = getSupabaseClient();
  // SECURITY DEFINER RPC locked to the tracking_* columns — a bare UPDATE policy on
  // `devices` can't restrict columns, which would let an org member rewrite
  // organization_id (cross-org move) or device identity.
  const { error } = await supabase.rpc('update_device_settings', {
    p_device_id: deviceId,
    p_tracking_accuracy: settings.tracking_accuracy,
    p_tracking_activity_type: settings.tracking_activity_type,
    p_tracking_distance_interval_m: settings.tracking_distance_interval_m,
  });
  if (error) throw error;
}

export async function updateDeviceDetails(
  deviceId: string,
  details: {
    device_name: string;
    first_name: string | null;
    last_name: string | null;
    phone_number: string | null;
    serial_number: string | null;
  }
): Promise<void> {
  const supabase = getSupabaseClient();
  // SECURITY DEFINER RPC locked to the editable identity columns — same reasoning as
  // `update_device_settings`: a bare UPDATE policy on `devices` can't restrict columns,
  // which would let an org member rewrite `organization_id` (cross-org move). This RPC
  // never touches `organization_id` or `device_id` (the device-set identifier). NULL
  // clears the nullable fields (full update).
  const { error } = await supabase.rpc('update_device_details', {
    p_device_uuid: deviceId,
    p_device_name: details.device_name,
    p_first_name: details.first_name,
    p_last_name: details.last_name,
    p_phone_number: details.phone_number,
    p_serial_number: details.serial_number,
  });
  if (error) throw error;
}

export async function deleteDevice(deviceUuid: string): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc('delete_device', {
    p_device_uuid: deviceUuid,
  });
  if (error) throw error;
}

export async function archiveDevice(deviceUuid: string): Promise<string> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc('archive_device', {
    p_device_uuid: deviceUuid,
  });
  if (error) throw error;
  return new Date().toISOString();
}

export async function unarchiveDevice(deviceUuid: string): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc('unarchive_device', {
    p_device_uuid: deviceUuid,
  });
  if (error) throw error;
}
